import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, Phone, MessageCircle } from 'lucide-react';
import { ZYLUS_SYSTEM_PROMPT } from '../data/zylusKnowledge';
interface ChatMsg {
  id: number;
  from: 'bot' | 'user';
  text: string;
}
const PHONE = '07061032706';
const PHONE_INTL = '2347061032706';
const quickReplies = ['What estates do you have?', 'Pricing & payment plans', 'How do I book an inspection?', 'I am buying from abroad'];
// Get OpenAI API key from environment (injected via Magic Patterns Integrations)
const OPENAI_KEY = (import.meta as any).env?.VITE_OPENAI_API_KEY || (import.meta as any).env?.OPENAI_API_KEY || '';
async function askZee(history: ChatMsg[], userMessage: string): Promise<string> {
  if (!OPENAI_KEY) {
    return "I'm having trouble reaching my brain right now 🤖 Please call us on 07061032706 or tap WhatsApp in the dock — a human will help you straight away.";
  }
  const messages = [{
    role: 'system',
    content: ZYLUS_SYSTEM_PROMPT
  }, ...history.slice(-8).map(m => ({
    role: m.from === 'user' ? 'user' : 'assistant',
    content: m.text
  })), {
    role: 'user',
    content: userMessage
  }];
  try {
    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${OPENAI_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages,
        temperature: 0.6,
        max_tokens: 350
      })
    });
    if (!res.ok) {
      const err = await res.text();
      console.error('OpenAI error:', err);
      throw new Error('AI request failed');
    }
    const data = await res.json();
    return data?.choices?.[0]?.message?.content?.trim() || "Sorry, I didn't catch that. Could you rephrase?";
  } catch (e) {
    console.error(e);
    return "I'm having a brief hiccup 🤖 In the meantime, please call 07061032706 or message us on WhatsApp — we're here to help.";
  }
}
export function FloatingActions() {
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([{
    id: 1,
    from: 'bot',
    text: "Hi 👋 I'm Zee — your Zylus property assistant. Ask me anything about our estates, pricing, payment plans, or how to invest from abroad."
  }]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const open = () => setChatOpen(true);
    window.addEventListener('open-zee-chat', open);
    return () => window.removeEventListener('open-zee-chat', open);
  }, []);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, chatOpen, isThinking]);
  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isThinking) return;
    const userMsg: ChatMsg = {
      id: Date.now(),
      from: 'user',
      text: trimmed
    };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setInput('');
    setIsThinking(true);
    const reply = await askZee(messages, trimmed);
    setMessages(m => [...m, {
      id: Date.now() + 1,
      from: 'bot',
      text: reply
    }]);
    setIsThinking(false);
  };
  return <AnimatePresence data-id="element-190">
      {chatOpen && <motion.div initial={{
      opacity: 0,
      y: 30,
      scale: 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 30,
      scale: 0.9
    }} transition={{
      type: 'spring',
      stiffness: 280,
      damping: 26
    }} className="fixed bottom-24 right-5 z-50 w-[calc(100%-2.5rem)] max-w-sm h-[540px] bg-white rounded-3xl shadow-2xl border border-ink/10 flex flex-col overflow-hidden" data-id="element-191">
          {/* Header */}
          <div className="bg-ink text-white p-4 flex items-center gap-3 relative overflow-hidden" data-id="element-192">
            <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 30,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -bottom-12 -right-12 w-32 h-32 rounded-full border border-gold/20" data-id="element-193" />
            <div className="relative w-10 h-10 rounded-full bg-gold flex items-center justify-center shrink-0" data-id="element-194">
              <Sparkles className="w-5 h-5 text-ink" fill="currentColor" data-id="element-195" />
              <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-400 border-2 border-ink" data-id="element-196" />
            </div>
            <div className="relative flex-1 min-w-0" data-id="element-197">
              <div className="font-serif text-lg leading-tight" data-id="element-198">Zee</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gold" data-id="element-199">
                AI Property Assistant · Online
              </div>
            </div>
            <button onClick={() => setChatOpen(false)} aria-label="Close chat" className="relative w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-id="element-200">
              <X className="w-4 h-4 text-white" data-id="element-201" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-bone/40" data-id="element-202">
            {messages.map(m => <motion.div key={m.id} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-203">
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.from === 'user' ? 'bg-ink text-white rounded-br-md' : 'bg-white text-ink border border-ink/5 rounded-bl-md shadow-sm'}`} data-id="element-204">
                  {m.text}
                </div>
              </motion.div>)}

            {/* Typing indicator */}
            {isThinking && <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex justify-start" data-id="element-205">
                <div className="bg-white text-ink border border-ink/5 rounded-2xl rounded-bl-md shadow-sm px-4 py-3 flex items-center gap-1.5" data-id="element-206">
                  {[0, 1, 2].map(i => <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-gold" animate={{
              y: [0, -4, 0],
              opacity: [0.4, 1, 0.4]
            }} transition={{
              duration: 0.9,
              repeat: Infinity,
              delay: i * 0.15
            }} data-id="element-207" />)}
                </div>
              </motion.div>}

            {messages.length <= 1 && !isThinking && <div className="pt-2 space-y-2" data-id="element-208">
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink/40 px-1" data-id="element-209">
                  Quick questions
                </div>
                {quickReplies.map(q => <button key={q} onClick={() => send(q)} className="w-full text-left text-sm px-4 py-2.5 rounded-xl bg-white hover:bg-gold hover:text-ink border border-ink/10 hover:border-gold transition-colors" data-id="element-210">
                    {q}
                  </button>)}
              </div>}
          </div>

          {/* Quick contact strip */}
          <div className="px-3 py-2 bg-white border-t border-ink/5 flex items-center gap-2" data-id="element-211">
            <a href={`tel:${PHONE}`} className="flex-1 flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest py-2 rounded-lg bg-bone hover:bg-gold text-ink transition-colors" data-id="element-212">
              <Phone className="w-3 h-3" data-id="element-213" /> Call
            </a>
            <a href={`https://wa.me/${PHONE_INTL}`} className="flex-1 flex items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest py-2 rounded-lg bg-bone hover:bg-gold text-ink transition-colors" data-id="element-214">
              <MessageCircle className="w-3 h-3" data-id="element-215" /> WhatsApp
            </a>
          </div>

          {/* Input */}
          <form onSubmit={e => {
        e.preventDefault();
        send(input);
      }} className="p-3 border-t border-ink/5 bg-white flex items-center gap-2" data-id="element-216">
            <input value={input} onChange={e => setInput(e.target.value)} placeholder={isThinking ? 'Zee is thinking…' : 'Ask Zee anything…'} disabled={isThinking} className="flex-1 bg-bone/50 px-4 py-2.5 rounded-full text-sm focus:outline-none focus:bg-bone/80 transition-colors placeholder:text-ink/40 disabled:opacity-60" data-id="element-217" />
            <button type="submit" aria-label="Send message" className="shrink-0 w-10 h-10 rounded-full bg-gold hover:bg-gold-dark text-ink flex items-center justify-center transition-colors disabled:opacity-50" disabled={!input.trim() || isThinking} data-id="element-218">
              <Send className="w-4 h-4" data-id="element-219" />
            </button>
          </form>
        </motion.div>}
    </AnimatePresence>;
}